import { useEffect, useState, type FormEvent } from 'react';
import type { CreateContractInput, UpdateContractInput } from '@armard/shared';
import { formatCurrency } from '@armard/shared';
import { Button, FormSaveBar, GlassPanel, Input, PageHeader, Select } from '../ui';
import { useCustomer, type Customer } from '../../hooks/useCustomers';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';
import { CustomerSelect } from '../invoices/CustomerSelect';
import {
  billingIntervalLabels,
  type Contract,
  type ContractBillingInterval,
} from '../../hooks/useContracts';

type ItemDraft = {
  key: string;
  description: string;
  longDescription: string;
  quantity: number;
  unit: string;
  unitPrice: number;
};

function newItem(): ItemDraft {
  return {
    key: crypto.randomUUID(),
    description: '',
    longDescription: '',
    quantity: 1,
    unit: 'Stück',
    unitPrice: 0,
  };
}

interface ContractFormProps {
  initial?: Contract;
  initialCustomerId?: string;
  title: string;
  submitLabel: string;
  isSaving: boolean;
  onCancel: () => void;
  onSave: (values: CreateContractInput | UpdateContractInput) => Promise<void>;
}

export function ContractForm({
  initial,
  initialCustomerId,
  title,
  submitLabel,
  isSaving,
  onCancel,
  onSave,
}: ContractFormProps) {
  const customerIdSeed = initial?.customerId ?? initialCustomerId ?? '';
  const { data: selectedCustomer } = useCustomer(customerIdSeed);
  const [titleValue, setTitleValue] = useState(initial?.title ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [customerId, setCustomerId] = useState(customerIdSeed);
  const [selectedCustomerValue, setSelectedCustomerValue] = useState<Customer | null>(null);
  useEffect(() => {
    if (selectedCustomer?.id === customerId) setSelectedCustomerValue(selectedCustomer);
  }, [customerId, selectedCustomer]);
  const [startDate, setStartDate] = useState(
    initial?.startDate ?? new Date().toISOString().slice(0, 10)
  );
  const [endDate, setEndDate] = useState(initial?.endDate ?? '');
  const [billingInterval, setBillingInterval] = useState<ContractBillingInterval>(
    initial?.billingInterval ?? 'monthly'
  );
  const [billingDayOfMonth, setBillingDayOfMonth] = useState(initial?.billingDayOfMonth ?? 1);
  const [vatRate, setVatRate] = useState(Number(initial?.vatRate ?? 19));
  const [customerReference, setCustomerReference] = useState(initial?.customerReference ?? '');
  const [invoicePrefix, setInvoicePrefix] = useState(initial?.invoicePrefix ?? '');
  const [autoGenerate, setAutoGenerate] = useState(initial?.autoGenerate ?? false);
  const [items, setItems] = useState<ItemDraft[]>(
    initial?.items?.map((item) => ({
      key: item.id || crypto.randomUUID(),
      description: item.description,
      longDescription: item.longDescription ?? '',
      quantity: Number(item.quantity),
      unit: item.unit ?? 'Stück',
      unitPrice: Number(item.unitPrice),
    })) ?? [newItem()]
  );
  const [editVersion] = useState(initial?.version);
  const [formError, setFormError] = useState('');
  useUnsavedChanges({
    titleValue,
    description,
    customerId,
    startDate,
    endDate,
    billingInterval,
    billingDayOfMonth,
    vatRate,
    customerReference,
    invoicePrefix,
    autoGenerate,
    items,
  });

  const setItem = (key: string, field: keyof Omit<ItemDraft, 'key'>, value: string | number) =>
    setItems((current) =>
      current.map((item) => (item.key === key ? { ...item, [field]: value } : item))
    );
  const net = items.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSaving) return;
    setFormError('');
    if (!customerId) return setFormError('Bitte wählen Sie einen Kunden.');
    if (!titleValue.trim()) return setFormError('Bitte geben Sie einen Vertragstitel ein.');
    if (items.some((item) => !item.description.trim()))
      return setFormError('Bitte beschreiben Sie jede Position.');
    if (items.some((item) => !Number.isFinite(item.quantity) || item.quantity <= 0))
      return setFormError('Die Menge jeder Position muss größer als 0 sein.');
    if (items.some((item) => !Number.isFinite(item.unitPrice) || item.unitPrice < 0))
      return setFormError('Der Preis darf nicht negativ sein.');

    const common = {
      title: titleValue.trim(),
      description: description || undefined,
      customerId,
      startDate,
      endDate: endDate || undefined,
      billingInterval,
      billingDayOfMonth,
      vatRate,
      customerReference: customerReference || undefined,
      invoicePrefix: invoicePrefix || undefined,
      autoGenerate,
      items: items.map((item, index) => ({
        position: index + 1,
        description: item.description.trim(),
        longDescription: item.longDescription || undefined,
        quantity: item.quantity,
        unit: item.unit.trim() || 'Stück',
        unitPrice: item.unitPrice,
      })),
    };
    await onSave(
      initial
        ? {
            ...common,
            description: description || null,
            endDate: endDate || null,
            customerReference: customerReference || null,
            invoicePrefix: invoicePrefix || null,
            version: editVersion,
          }
        : common
    );
  };

  return (
    <form onSubmit={submit} className="space-y-6">
      <PageHeader
        backTo="/contracts"
        backLabel="Verträge"
        title={title}
        subtitle={initial?.contractNumber}
      />
      <fieldset
        disabled={isSaving}
        aria-busy={isSaving}
        className="min-w-0 border-0 p-0 m-0 space-y-6"
      >
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <GlassPanel>
              <h2 className="text-lg font-medium text-white mb-4">Grunddaten</h2>
              <div className="space-y-4">
                <CustomerSelect
                  value={customerId}
                  selectedCustomer={selectedCustomerValue}
                  onChange={(id, customer) => {
                    setCustomerId(id);
                    setSelectedCustomerValue(customer);
                  }}
                />
                <Input
                  name="contract-title"
                  label="Titel *"
                  value={titleValue}
                  onChange={(e) => setTitleValue(e.target.value)}
                  required
                />
                <label className="block text-sm text-white/60">
                  Beschreibung
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                    className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white"
                  />
                </label>
                <Input
                  name="contract-reference"
                  label="Kundenreferenz"
                  value={customerReference}
                  onChange={(e) => setCustomerReference(e.target.value)}
                />
                <Input
                  name="contract-invoice-prefix"
                  label="Rechnungspräfix"
                  value={invoicePrefix}
                  onChange={(e) => setInvoicePrefix(e.target.value)}
                />
              </div>
            </GlassPanel>
            <GlassPanel>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-medium text-white">Positionen</h2>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setItems((current) => [...current, newItem()])}
                >
                  + Position
                </Button>
              </div>
              <div className="space-y-4">
                {items.map((item, index) => (
                  <div key={item.key} className="p-4 bg-white/5 rounded-lg space-y-3">
                    <div className="flex justify-between text-sm text-white/50">
                      <span>Position {index + 1}</span>
                      {items.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            setItems((current) => current.filter((row) => row.key !== item.key))
                          }
                        >
                          Entfernen
                        </Button>
                      )}
                    </div>
                    <Input
                      name={`contract-item-${index}-description`}
                      label="Beschreibung *"
                      value={item.description}
                      onChange={(e) => setItem(item.key, 'description', e.target.value)}
                      required
                    />
                    <Input
                      name={`contract-item-${index}-long-description`}
                      label="Zusatztext"
                      value={item.longDescription}
                      onChange={(e) => setItem(item.key, 'longDescription', e.target.value)}
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <Input
                        name={`contract-item-${index}-quantity`}
                        label="Menge *"
                        type="number"
                        min="0.001"
                        step="0.001"
                        value={item.quantity}
                        onChange={(e) => setItem(item.key, 'quantity', Number(e.target.value))}
                        required
                      />
                      <Input
                        name={`contract-item-${index}-unit`}
                        label="Einheit"
                        maxLength={20}
                        value={item.unit}
                        onChange={(e) => setItem(item.key, 'unit', e.target.value)}
                        hint="Stunden, Tage, Stück, pauschal oder km für die Rechnungsumwandlung"
                      />
                      <Input
                        name={`contract-item-${index}-price`}
                        label="Einzelpreis netto"
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.unitPrice}
                        onChange={(e) => setItem(item.key, 'unitPrice', Number(e.target.value))}
                        required
                      />
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-4 border-t border-white/10 space-y-1 text-right">
                <p className="text-white/60">Netto je Periode: {formatCurrency(net)}</p>
                <p className="text-white/60">
                  MwSt. ({vatRate}%): {formatCurrency((net * vatRate) / 100)}
                </p>
                <p className="text-lg font-semibold text-white">
                  Gesamt: {formatCurrency(net * (1 + vatRate / 100))}
                </p>
              </div>
            </GlassPanel>
          </div>
          <div className="space-y-6">
            <GlassPanel>
              <h2 className="text-lg font-medium text-white mb-4">Laufzeit und Abrechnung</h2>
              <div className="space-y-4">
                <Input
                  name="contract-start"
                  label="Vertragsbeginn *"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  required
                />
                <Input
                  name="contract-end"
                  label="Vertragsende"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  hint="Leer = unbefristet"
                />
                <Select
                  name="contract-interval"
                  label="Intervall"
                  value={billingInterval}
                  onChange={(e) => setBillingInterval(e.target.value as ContractBillingInterval)}
                  options={Object.entries(billingIntervalLabels).map(([value, label]) => ({
                    value,
                    label,
                  }))}
                />
                <Input
                  name="contract-billing-day"
                  label="Abrechnungstag (1–28)"
                  type="number"
                  min="1"
                  max="28"
                  value={billingDayOfMonth}
                  onChange={(e) =>
                    setBillingDayOfMonth(Math.min(28, Math.max(1, Number(e.target.value) || 1)))
                  }
                />
                <Select
                  name="contract-vat"
                  label="MwSt.-Satz"
                  value={String(vatRate)}
                  onChange={(e) => setVatRate(Number(e.target.value))}
                  options={[
                    { value: '19', label: '19%' },
                    { value: '7', label: '7%' },
                    { value: '0', label: '0%' },
                  ]}
                />
                <label className="flex items-center gap-3 text-sm text-white/70">
                  <input
                    type="checkbox"
                    checked={autoGenerate}
                    onChange={(e) => setAutoGenerate(e.target.checked)}
                    className="w-5 h-5"
                  />
                  Automatisch erstellen
                </label>
              </div>
            </GlassPanel>
          </div>
        </div>
        {formError && (
          <p role="alert" className="text-sm text-red-400">
            {formError}
          </p>
        )}
        <FormSaveBar>
          <Button type="button" variant="secondary" onClick={onCancel} disabled={isSaving}>
            Abbrechen
          </Button>
          <Button type="submit" disabled={isSaving}>
            {isSaving ? 'Speichern...' : submitLabel}
          </Button>
        </FormSaveBar>
      </fieldset>
    </form>
  );
}
